import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { IntlKey } from 'locales';
import { Ref, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PinState } from 'routes/essential-workspace/components/right-panel/draggable-list/draggable-list-item';
import { LayoutField } from 'routes/essential-workspace/state/model';
import { Width } from 'state/product-description/types';
import { FieldId } from 'state/userPreferences/userPreferences.model';

interface LayoutFieldListItemProps {
  field: LayoutField;
  handleRef?: Ref<HTMLElement>;
  pinState: PinState;
  isDraggable: boolean;
  disabledPinVariant?: 'filled' | 'outlined';
  onTogglePin?: (fieldId: FieldId) => void;
  onUpdateWidth?: (fieldId: FieldId, width: Width) => void;

  showForbiddenTooltip?: boolean;
  forbiddenTooltipTrigger?: number;
}

const WIDTH_OPTIONS: Array<{ label: 'Half' | 'Full'; value: Width }> = [
  { label: 'Half', value: 'half' },
  { label: 'Full', value: 'full' },
];

const PIN_BUTTON_BASE_CLASS = 'btn btn-link p-0 text-decoration-none essential-right-panel-pin-btn';

function getPinIconClassName(
  pinState: PinState,
  isNonDraggableInactive: boolean,
  disabledPinVariant: 'filled' | 'outlined',
) {
  if (pinState === 'active') {
    return 'icon text-info';
  }

  if (pinState === 'inactive') {
    return 'icon text-secondary opacity-75';
  }

  return classNames(
    'icon text-secondary',
    disabledPinVariant === 'outlined' && 'icon-outlined',
    isNonDraggableInactive && 'essential-right-panel-opacity-20',
  );
}

export const LayoutFieldListItem = ({
  pinState,
  isDraggable,
  field,
  handleRef,
  onTogglePin,
  onUpdateWidth,
  disabledPinVariant = 'filled',
  showForbiddenTooltip,
  forbiddenTooltipTrigger,
}: LayoutFieldListItemProps) => {
  const isPinDisabled = pinState === 'disabled';
  const isNonDraggableInactive = !isDraggable && !field.isPinned;
  const isWidthDisabled = !field.isOrderable;

  const pinButtonClassName = classNames(PIN_BUTTON_BASE_CLASS, isPinDisabled && 'disabled');

  const pinIconClassName = getPinIconClassName(pinState, isNonDraggableInactive, disabledPinVariant);

  const dragIconClassName = classNames(
    'icon icon-outlined essential-right-panel-item-handle',
    !isDraggable && 'text-secondary',
    isDraggable && 'cursor-grab',
    isNonDraggableInactive && 'essential-right-panel-opacity-20',
  );

  return (
    <div className="card-body bg-white bg-opacity-05 d-flex align-items-center justify-content-between gap-3 px-3 py-2">
      <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
        <i className={dragIconClassName} ref={handleRef} aria-hidden="true">
          drag_indicator
        </i>
        <span className="fs-5 fw-normal lh-sm text-body text-truncate">
          <FormattedMessage id={`form.${field.label}` as IntlKey} />
        </span>
      </div>
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <div className="btn-group" role="radiogroup" aria-label={`Width for ${field.label}`}>
          {WIDTH_OPTIONS.map(option => {
            const isSelected = field.width === option.value;

            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={classNames(
                  'btn btn-flat-secondary bg-transparent px-2 py-1 essential-right-panel-width-btn',
                  isSelected ? 'btn-dark' : 'btn-light',
                )}
                disabled={isWidthDisabled}
                onClick={() => {
                  if (!isWidthDisabled && onUpdateWidth) {
                    onUpdateWidth(field.fieldId, option.value);
                  }
                }}
              >
                {option.label === 'Half' ? <HalfWidthIcon /> : <FullWidthIcon />}
              </button>
            );
          })}
        </div>
        <div className="essential-right-panel-pin-slot d-flex align-items-center justify-content-center">
          <PinnedButton
            pinButtonClassName={pinButtonClassName}
            pinIconClassName={pinIconClassName}
            isPinDisabled={isPinDisabled}
            field={field}
            onTogglePin={onTogglePin}
            showForbiddenTooltip={showForbiddenTooltip}
            forbiddenTooltipTrigger={forbiddenTooltipTrigger}
          />
        </div>
      </div>
    </div>
  );
};

type PinnedButtonProps = {
  pinButtonClassName: string;
  pinIconClassName: string;
  isPinDisabled: boolean;
  field: LayoutField;
  showForbiddenTooltip?: boolean;
  forbiddenTooltipTrigger?: number;
  onTogglePin?: (fieldId: FieldId) => void;
};

const PinnedButton = ({
  pinButtonClassName,
  pinIconClassName,
  isPinDisabled,
  field,
  onTogglePin,
  showForbiddenTooltip = false,
  forbiddenTooltipTrigger,
}: PinnedButtonProps) => {
  const button = (
    <button
      type="button"
      className={classNames(pinButtonClassName, 'd-inline-flex align-items-center justify-content-center')}
      aria-label={
        isPinDisabled ? `Pin disabled for ${field.label}` : `${field.isPinned ? 'Unpin' : 'Pin'} ${field.label}`
      }
      aria-pressed={isPinDisabled ? undefined : field.isPinned}
      disabled={isPinDisabled}
      onClick={() => {
        if (!isPinDisabled && onTogglePin) {
          onTogglePin(field.fieldId);
        }
      }}
    >
      <i className={pinIconClassName} aria-hidden="true">
        push_pin
      </i>
    </button>
  );

  if (isPinDisabled) {
    return button;
  }

  return (
    <ForbiddenDropTooltip show={showForbiddenTooltip} trigger={forbiddenTooltipTrigger}>
      <Tooltip
        position="top"
        contents={
          <div data-e2e="tooltip-element">
            <FormattedMessage id="essential.workspace.right.panel.main-list.pin-tooltip" />
          </div>
        }
      >
        {button}
      </Tooltip>
    </ForbiddenDropTooltip>
  );
};

type ForbiddenDropTooltipProps = {
  show: boolean;
  delayMs?: number;
  children: React.ReactNode;
  trigger?: number;
};

const ForbiddenDropTooltip = ({ show, trigger, delayMs = 2000, children }: ForbiddenDropTooltipProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show || !trigger) {
      setVisible(false);
      return;
    }

    setVisible(true);

    const timeout = window.setTimeout(() => {
      setVisible(false);
    }, delayMs);

    return () => clearTimeout(timeout);
  }, [show, trigger, delayMs]);

  return (
    <div className="position-relative d-inline-flex">
      {visible && (
        <div
          role="tooltip"
          aria-live="assertive"
          className="tooltip bs-tooltip-top show position-absolute forbidden-drop-tooltip"
        >
          <div className="tooltip-inner px-2 py-1">
            <FormattedMessage id="essential.workspace.right.panel.main-list.pin-tooltip" />
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

const FullWidthIcon = () => (
  <>
    <i className="icon text-secondary text-opacity-50">
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3">
        <path d="M120-200v-240h320v240H120Zm400 0v-240h320v240H520ZM120-520v-240h720v240H120Z" />
      </svg>
    </i>
    <div className="icon-highlight">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M3 10V6C3 5.71667 3.09583 5.47917 3.2875 5.2875C3.47917 5.09583 3.71667 5 4 5H20C20.2833 5 20.5208 5.09583 20.7125 5.2875C20.9042 5.47917 21 5.71667 21 6V10C21 10.2833 20.9042 10.5208 20.7125 10.7125C20.5208 10.9042 20.2833 11 20 11H4C3.71667 11 3.47917 10.9042 3.2875 10.7125C3.09583 10.5208 3 10.2833 3 10Z"
          fill="var(--bs-primary)"
        />
      </svg>
    </div>
  </>
);

const HalfWidthIcon = () => (
  <>
    <i className="icon text-secondary text-opacity-50">
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3">
        <path d="M120-200v-240h320v240H120Zm400 0v-240h320v240H520ZM120-520v-240h320v240H120Zm400 0v-240h320v240H520Z" />
      </svg>
    </i>
    <div className="icon-highlight">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M2.98926 10V6C2.98926 5.71667 3.08509 5.47917 3.27676 5.2875C3.46842 5.09583 3.70592 5 3.98926 5H9.98926C10.2726 5 10.5101 5.09583 10.7018 5.2875C10.8934 5.47917 10.9893 5.71667 10.9893 6V10C10.9893 10.2833 10.8934 10.5208 10.7018 10.7125C10.5101 10.9042 10.2726 11 9.98926 11H3.98926C3.70592 11 3.46842 10.9042 3.27676 10.7125C3.08509 10.5208 2.98926 10.2833 2.98926 10Z"
          fill="var(--bs-primary)"
        />
      </svg>
    </div>
  </>
);
